/**
 * Card Component — Soorgaa Design System
 * 
 * Flat, sharp, architectural container component with tokens-only styling.
 * Used for modular items and panels. Never nest bordered cards inside bordered cards.
 */
import React from "react";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "child" | "flat" | "interactive";
  children: React.ReactNode;
  className?: string;
}

export function Card({
  variant = "default",
  children,
  className = "",
  ...props
}: CardProps) {
  const variantClasses = {
    default: "bg-card border border-border shadow-soft",
    child: "bg-card-child border border-border shadow-soft",
    flat: "bg-card-stat border border-border",
    interactive:
      "group bg-card border border-border shadow-soft hover:border-primary/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-200 cursor-pointer motion-reduce:transition-none motion-reduce:hover:transform-none focus-visible:ring-2 ring-ring",
  };

  return (
    <div
      className={`rounded-tile p-4 sm:p-5 text-card-foreground ${variantClasses[variant]} ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  actions,
  className = "",
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-start justify-between gap-3 mb-3.5 pb-2.5 border-b border-border/60 ${className}`.trim()}>
      <div>
        <h3 className="text-sm sm:text-base font-bold text-foreground tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  );
}
